/**
 * Base44 / Emergent–style builder prompt: plain sections, no Markdown # headings.
 *
 * @param {Record<string, any>} normalized output of normalizeOpportunityForBuilderPrompt
 */
export function generateBuilderPrompt(normalized) {
  const n = normalized || {};
  const importantExtras = Array.isArray(n.excludedFeatures)
    ? n.excludedFeatures.map((line) => `- ${String(line).replace(/^[-•\s]+/, "")}`).join("\n")
    : "";

  const goalExtra = n.isWebAppBuildable
    ? ""
    : "\n\nNote: the original idea mentions heavy infra — ship the smallest honest web MVP that demonstrates the same user value without that infra.";

  const complexityTarget =
    n.buildComplexity === "low"
      ? "Target <= 1 week for first shippable version."
      : n.buildComplexity === "high"
        ? "Target <= 3 weeks for first shippable version with strict scope control."
        : "Target <= 2 weeks for first shippable version.";

  return `Build a production-minded but narrow MVP web app.

Name: ${n.cleanName || "Web app"}

Goal:
${n.cleanOneLiner || "Ship one clear user outcome in a browser."}${goalExtra}

Target users:
${n.targetUser || "Small teams"}

Builder intent:
${n.builderIntent || "generic_tool"}

Complexity guidance:
${complexityTarget}

---

Deliverables (must all be included):

1) Product spec
2) Technical architecture
3) Data model
4) API contract
5) Frontend screens
6) Step-by-step implementation plan
7) QA checklist
8) Deployment checklist
9) Post-MVP roadmap

---

Product scope (MVP v1):

1. Homepage with:
   - clear headline
   - short value proposition
   - primary input/action for: ${n.coreUserAction || "the one main input"}
   - one clear CTA button
   - small trust section (how it works in 3 bullets)

2. Core workflow:
   - user submits input
   - system processes input
   - output is saved when relevant
   - user can revisit previous outputs (if persistence is enabled)
   - ${n.coreSystemAction || "Run the core logic and persist the minimum data if needed."}

3. Result screen:
   - ${n.coreResult || "A clear outcome the user understands immediately."}
   - confidence/status indicator
   - suggested next action
   - copy/export button for result text when relevant

4. Billing gates:
   - free tier with strict limit
   - paid tier unlocks meaningful value
   - soft paywall after user sees value first

---

Frontend requirements:

- Next.js App Router (or equivalent in the builder)
- Tailwind or equivalent utility styling
- Responsive layout (mobile first)
- Loading, empty, error, and success states on every core screen
- Clear forms with validation messages
- Reusable components: Button, Input, Card, Badge, Modal, Toast

Required screens:
- / Landing + CTA
- /app Main workflow
- /results/:id Result detail
- /pricing Pricing + upgrade CTA
- /auth (only if persistence requires accounts)
- /settings (minimal: account + billing link)

---

Backend/API requirements:

- Create API routes for:
  - POST /api/process for main action
  - GET /api/results list user results
  - GET /api/results/:id single result
  - POST /api/billing/checkout create checkout session
  - POST /api/billing/webhook receive billing events
- Validate all inputs server-side
- Return consistent JSON shape: { ok, data, error }
- Add basic rate limit protection on main write endpoints
- Add structured logging for failures

---

Data model (minimum):

- users
  - id, email, created_at, plan, stripe_customer_id
- projects_or_results
  - id, user_id, input_payload, output_payload, status, created_at, updated_at
- usage_events
  - id, user_id, event_type, units, created_at
- subscriptions
  - id, user_id, provider, provider_subscription_id, status, current_period_end

Use migrations and include SQL/schema definitions.

---

Monetization and pricing:

- free: limited usage (clear numeric limit)
- paid: unlimited or much higher limits
- pricing hint: ${n.recommendedMonetization || "suggested $19/mo; adjust to the niche"}
- enforce limits on backend (not frontend only)
- show remaining free quota in UI

---

Security and reliability:

- Never trust client input
- Protect private routes and user-owned data by user_id checks
- Add request timeout handling and user-friendly fallback messages
- Add idempotency where needed for billing-related endpoints
- No secrets in client code

---

QA checklist (must implement and run):

- user can finish main action in under 2 minutes
- all required screens render without console errors
- invalid input returns readable error
- network failure shows retry path
- free limit is enforced
- paid upgrade path works end-to-end
- data persists and is user-scoped correctly

---

Deployment checklist:

- add env variables template (.env.example)
- production build succeeds with zero type/lint errors
- database migrations applied
- webhook endpoint configured
- analytics + error monitoring initialized (minimal)

---

Out of scope for MVP (do not build now):

- complex analytics dashboards
- multi-tenant admin panels
- enterprise RBAC
- native mobile apps
- Kubernetes/microservices
- unrelated scanner/workflow branches
- any features outside the main user outcome
${importantExtras ? `${importantExtras}\n` : ""}

---

Implementation order (strict):

1) schema + migrations
2) API routes and validation
3) core UI flow
4) persistence and user scoping
5) pricing page + billing integration
6) polish states (loading/error/empty)
7) QA pass + deployment

---

Internal ref:

${n.opportunityId || "unknown"}${n.marketHintLine ? `\n${n.marketHintLine}` : ""}
`;
}
